/**
 * Client-side image compression.
 *
 * Why: modern phones take 5–15MB photos (iPhone 15/16 JPEG, Android HEIC-
 * auto-converted). Our backend caps at 10MB, so a raw ProRAW shot or a long
 * exposure can be silently rejected on mobile where toasts are easy to miss.
 * The avatars we render on the Big Screen are at most ~400 px wide, so
 * downsizing to 2048 px on the long side with JPEG 0.85 loses nothing visible
 * while keeping every upload comfortably under ~2 MB.
 *
 * Design decisions:
 *   - MAX_DIM = 2048 → still crisp if the manager zooms in on the crop dialog
 *     (which caps crop output at 1024 anyway).
 *   - QUALITY = 0.85 → sweet spot for portraits; dropping to 0.8 would show
 *     block artifacts on skin tones at display size.
 *   - If the file is already a small JPEG (< 500 KB) and under MAX_DIM we skip
 *     re-encoding to preserve the exact original bytes.
 *   - Transparent PNGs are preserved as PNG (no quality knob) so logos / cutout
 *     avatars don't gain an ugly background.
 */
// 1024 is the final crop size (see PhotoCropDialog) and matches the max
// portrait size rendered anywhere on the Big Screen. Downsizing to 1024 at
// this step means react-easy-crop only ever holds a ~1 MB image in RAM
// (vs ~16 MB at 2048) — this is the fix for the "Mémoire insuffisante"
// alert on Samsung + Chrome Android, which has a much lower memory
// ceiling than iOS Safari.
const MAX_DIM = 1024;
const QUALITY = 0.85;
const SKIP_THRESHOLD = 300 * 1024; // 300 KB

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

// Safari / mobile browsers don't free the backing bitmap of a canvas until
// the element itself is garbage-collected, which under load (e.g. the user
// trying three photos in a row) blows past the WebKit memory ceiling.
// Resetting the dimensions to 0×0 immediately releases the pixel buffer.
const releaseCanvas = (canvas) => {
  try {
    canvas.width = 0;
    canvas.height = 0;
  } catch { /* no-op */ }
};

export async function compressImage(file) {
  if (!file) return file;

  // Infer output format from MIME when it is set; otherwise look at the
  // filename extension; fall back to JPEG. Samsung Chrome and some Android
  // cameras return `File { type: "" }` so we can't rely on MIME alone.
  const name = (file.name || "").toLowerCase();
  const isPng = file.type === "image/png" || /\.png$/.test(name);
  const outMime = isPng ? "image/png" : "image/jpeg";

  // Decode-test gate: if the browser can't turn these bytes into an Image
  // (truly not an image file, broken HEIC, etc.) `loadImage` rejects and
  // we propagate — the UI shows a clean error instead of silently doing
  // nothing. Previously `compressImage` swallowed decode errors and
  // returned the raw File, which then made the crop dialog spin forever.
  const img = await loadImage(file);

  // Fast-path: tiny JPEG with normal dimensions → upload as-is.
  if (!isPng && file.size < SKIP_THRESHOLD) {
    const ok = img.width <= MAX_DIM && img.height <= MAX_DIM;
    if (ok) {
      img.src = "";
      return file;
    }
  }

  const { width, height } = img;
  const scale = Math.min(1, MAX_DIM / Math.max(width, height));
  const targetW = Math.round(width * scale);
  const targetH = Math.round(height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, targetW, targetH);

  // Drop the source bitmap ASAP: on Safari the HTMLImageElement keeps the
  // decoded raster around and three successive 4000×3000 shots are enough
  // to trip the "memory insufficient" alert.
  img.src = "";

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, outMime, isPng ? undefined : QUALITY)
  );
  releaseCanvas(canvas);
  if (!blob) return file;

  const ext = isPng ? ".png" : ".jpg";
  const base = (file.name || "photo").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}${ext}`, { type: outMime, lastModified: Date.now() });
}

export default compressImage;
